import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import type { RequestError } from '@freelancer/datastore';
import {
  Datastore,
  ORDER_BY_OPTIONS_UNICODE_CI,
  OrderByDirection,
} from '@freelancer/datastore';
import type {
  DiscoverCollection,
  DiscoverCollectionsCollection,
  EnterprisesCollection,
  PoolModel,
  PoolsCollection,
  PredefinedDiscoverCollection,
  PredefinedDiscoverCollectionsCollection,
  User,
  UsersCollection,
  UsersFollowCollection,
} from '@freelancer/datastore/collections';
import { Pool } from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { LocalStorage } from '@freelancer/local-storage';
import { Assets } from '@freelancer/ui/assets';
import { trackById } from '@freelancer/ui/helpers';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkUnderline, LinkWeight } from '@freelancer/ui/link';
import {
  QueryParamsHandling,
  TabsDirection,
  TabsSize,
} from '@freelancer/ui/tabs';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DiscoverCollectionTypeApi } from 'api-typings/users/users';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

@UntilDestroy({ className: 'MyListsComponent' })
@Component({
  selector: 'fl-my-lists',
  template: `
    <fl-scrollable-content class="MyListsScrollableContent">
      <ng-container
        *ngIf="(userCollectionLoading$ | flAsync) === false; else loadingState"
      >
        <!-- Talent pools and default user lists  -->
        <div
          *ngIf="showBookmarkedPosts"
          class="MyLists-listCollectionHeader"
        >
          <p class="text-small font-bold">
            <ng-container
              *ngIf="uiConfig.theme === 'deloitte'"
              i18n="My List Widget Talent List Title"
            >
              Talent
            </ng-container>
            <ng-container
              *ngIf="uiConfig.theme !== 'deloitte'"
              i18n="My List Widget User List Title"
            >
              Users
            </ng-container>
          </p>
        </div>
        <fl-tabs
          [direction]="TabsDirection.COLUMN_RIGHT"
          [size]="TabsSize.XSMALL"
          [truncate]="true"
        >
          <ng-container
            *ngIf="showPoolLists$ | flAsync"
            ngProjectAs="fl-tab-item"
          >
            <fl-tab-item
              *ngFor="
                let talentNetworkPool of talentNetworkPools$ | flAsync;
                trackBy: trackById
              "
              flTrackingLabel="PoolsCollectionClick"
              [flTrackingReferenceType]="'pool_id'"
              [flTrackingReferenceId]="talentNetworkPool.id"
              [iconName]="'ui-bird'"
              [iconColor]="IconColor.SECONDARY"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/pool/' + talentNetworkPool.seoUrl"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
              [titleText]="talentNetworkPool.displayName"
            ></fl-tab-item>

            <ng-container *ngIf="insourcePool$ | flAsync as insourcePool">
              <fl-tab-item
                flTrackingLabel="PoolsCollectionClick"
                [flTrackingReferenceType]="'pool_id'"
                [flTrackingReferenceId]="insourcePool.id"
                [imageSrc]="insourcePoolImage"
                [link]="'/lists/pool/' + insourcePool.seoUrl"
                [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                [titleText]="insourcePool.displayName"
              ></fl-tab-item>
            </ng-container>
          </ng-container>

          <fl-tab-item
            *ngIf="favoritesList$ | flAsync as favoritesList"
            flTrackingLabel="FavoritesCollectionClick"
            titleText="Favorites"
            i18n-titleText="My List Favorites Collection Title"
            [flTrackingReferenceType]="'collection_id'"
            [flTrackingReferenceId]="favoritesList.id"
            [iconColor]="IconColor.PRIMARY"
            [iconName]="'ui-heart-alt-v2'"
            [iconSize]="IconSize.SMALL"
            [link]="'/lists/' + favoritesList.seoUrl"
            [preserveBackButton]="preserveBackButton"
            [queryParamsHandling]="QueryParamsHandling.PRESERVE"
          ></fl-tab-item>

          <fl-tab-item
            *ngIf="myHiresList$ | flAsync as myHiresList"
            flTrackingLabel="MyHiresCollectionClick"
            titleText="My Hires"
            i18n-titleText="My List My Hires Collection Title"
            [flTrackingReferenceType]="'collection_id'"
            [flTrackingReferenceId]="myHiresList.id"
            [iconName]="'ui-suitcase-outline'"
            [iconSize]="IconSize.SMALL"
            [link]="'/lists/' + myHiresList.seoUrl"
            [preserveBackButton]="preserveBackButton"
            [queryParamsHandling]="QueryParamsHandling.PRESERVE"
          ></fl-tab-item>

          <ng-container
            *ngIf="myClientsList$ | flAsync as myClientsList"
            ngProjectAs="fl-tab-item"
          >
            <fl-tab-item
              *ngIf="myClientsList.itemCount > 0"
              flTrackingLabel="MyClientsCollectionClick"
              titleText="My Clients"
              i18n-titleText="My List My Clients Collection Title"
              [flTrackingReferenceType]="'collection_id'"
              [flTrackingReferenceId]="myClientsList.id"
              [iconName]="'ui-suitcase-outline'"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/' + myClientsList.seoUrl"
              [preserveBackButton]="preserveBackButton"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
            ></fl-tab-item>
          </ng-container>

          <fl-tab-item
            *ngIf="showFollowing$ | flAsync"
            flTrackingLabel="FollowingCollectionClick"
            titleText="Following"
            i18n-titleText="Following Users Collection Title"
            [iconName]="'ui-user-outline'"
            [iconSize]="IconSize.SMALL"
            [link]="'/users/employerWatchList.php'"
            [preserveBackButton]="preserveBackButton"
            [queryParamsHandling]="QueryParamsHandling.PRESERVE"
          ></fl-tab-item>

          <ng-container
            *ngIf="recentlyViewedList$ | flAsync as recentlyViewed"
            ngProjectAs="fl-tab-item"
          >
            <fl-tab-item
              flTrackingLabel="RecentlyViewedCollectionClick"
              titleText="Recently Viewed"
              i18n-titleText="My List Recently Viewed Collection Title"
              [flTrackingReferenceType]="'collection_id'"
              [flTrackingReferenceId]="recentlyViewed.id"
              [iconName]="'ui-show-outline'"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/' + recentlyViewed.seoUrl"
              [preserveBackButton]="preserveBackButton"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
            ></fl-tab-item>
          </ng-container>

          <ng-container
            *ngFor="
              let customList of customListsToShow$ | flAsync;
              let isLast = last;
              trackBy: trackById
            "
            ngProjectAs="fl-tab-item"
          >
            <fl-tab-item
              flTrackingLabel="CustomCollectionClick"
              [flTrackingReferenceType]="'collection_id'"
              [flMarginBottom]="isLast ? 'xxsmall' : 'none'"
              [flTrackingReferenceId]="customList.id"
              [iconName]="'ui-list-bullet'"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/' + customList.seoUrl"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
              [titleText]="customList.name"
            ></fl-tab-item>
          </ng-container>
        </fl-tabs>
        <div
          *ngIf="viewAllBottom && (showViewMore$ | flAsync)"
          class="MyLists-viewMore"
        >
          <fl-link
            flTrackingLabel="ViewMoreClick"
            i18n="My List Widget View More Text"
            [underline]="LinkUnderline.NEVER"
            (click)="viewMoreClick()"
          >
            View More
          </fl-link>
        </div>

        <!-- Bookmarked group posts -->
        <ng-container *ngIf="showBookmarkedPosts">
          <fl-hr
            *ngIf="showHorizontalSeparator"
            class="HorizontalLine"
          ></fl-hr>

          <div class="MyLists-listCollectionHeader">
            <p
              class="text-small font-bold"
              i18n="My List Widget Group Posts List Title"
            >
              Group posts
            </p>
          </div>
          <fl-tabs
            [direction]="TabsDirection.COLUMN_RIGHT"
            [size]="TabsSize.XSMALL"
            [truncate]="true"
          >
            <ng-container
              *ngIf="groupPostsList$ | flAsync as groupPosts"
              ngProjectAs="fl-tab-item"
            >
              <fl-tab-item
                flTrackingLabel="BookmarkedGroupPostsCollectionClick"
                titleText="Bookmarks"
                i18n-titleText="My List Groups Posts Collection Title"
                [flTrackingReferenceType]="'collection_id'"
                [flTrackingReferenceId]="groupPosts.id"
                [iconName]="'ui-bookmark-v2'"
                [iconSize]="IconSize.SMALL"
                [link]="'/lists/' + groupPosts.seoUrl"
                [preserveBackButton]="preserveBackButton"
                [queryParamsHandling]="QueryParamsHandling.PRESERVE"
              ></fl-tab-item>
            </ng-container>
          </fl-tabs>
        </ng-container>
      </ng-container>

      <!-- Loading state -->
      <ng-template #loadingState>
        <div class="MyLists-loadingState">
          <fl-spinner flTrackingLabel="My List Widget Loading"></fl-spinner>
        </div>
      </ng-template>

      <!-- Error state -->
      <fl-generic-error
        *ngIf="userCollectionError$ | flAsync as error"
        [error]="error"
      ></fl-generic-error>
    </fl-scrollable-content>
  `,
  styleUrls: ['./my-lists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyListsComponent implements OnInit, OnChanges {
  DiscoverCollectionTypeApi = DiscoverCollectionTypeApi;
  IconSize = IconSize;
  IconColor = IconColor;
  Feature = Feature;
  FontWeight = FontWeight;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  QueryParamsHandling = QueryParamsHandling;
  TabsDirection = TabsDirection;
  TabsSize = TabsSize;
  TextSize = TextSize;
  trackById = trackById;

  @Input() showPoolLists$ = of(false);
  @Input() customListLimit = 8;
  @Input() viewAllBottom = false;
  @Input() preserveBackButton = true;
  @Input() showBookmarkedPosts = true;
  @Input() showHorizontalSeparator = true;

  user$: Observable<User>;
  insourcePool$: Observable<PoolModel>;
  insourcePoolImage: string;
  talentNetworkPools$: Observable<readonly PoolModel[]>;
  favoritesList$: Observable<PredefinedDiscoverCollection>;
  myHiresList$: Observable<PredefinedDiscoverCollection>;
  myClientsList$: Observable<PredefinedDiscoverCollection>;
  showFollowing$: Observable<boolean>;
  recentlyViewedList$: Observable<PredefinedDiscoverCollection>;
  groupPostsList$: Observable<PredefinedDiscoverCollection>;
  customLists$: Observable<readonly DiscoverCollection[]>;
  userCollectionLoading$: Observable<boolean>;
  userCollectionError$: Observable<
    | RequestError<EnterprisesCollection>
    | RequestError<PoolsCollection>
    | RequestError<DiscoverCollectionsCollection>
    | RequestError<PredefinedDiscoverCollectionsCollection>
    | undefined
  >;
  showViewMore$: Observable<boolean>;

  customListsToShow$: Observable<readonly DiscoverCollection[]>;

  private listViewLimitSubject$ = new BehaviorSubject<number>(
    this.customListLimit,
  );
  listViewLimit$ = this.listViewLimitSubject$.asObservable();

  private shouldShowInitialLoadingStateSubject$ = new BehaviorSubject<boolean>(
    true,
  );
  shouldShowInitialLoadingState$ =
    this.shouldShowInitialLoadingStateSubject$.asObservable();

  private VIEW_MORE_INCREMENT = 10;

  constructor(
    private assets: Assets,
    private auth: Auth,
    private datastore: Datastore,
    private localStorage: LocalStorage,
    @Inject(UI_CONFIG) public uiConfig: UiConfig,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('customListLimit' in changes && this.customListLimit !== undefined) {
      this.listViewLimitSubject$.next(this.customListLimit);
    }
  }

  ngOnInit(): void {
    const userId$ = this.auth.getUserId().pipe(map(userId => toNumber(userId)));

    const userDocument = this.datastore.document<UsersCollection>(
      'users',
      userId$,
    );

    const enterpriseIds$ = userDocument.valueChanges().pipe(
      map(user =>
        user.isDeloitteDcUser && user.enterpriseIds
          ? user.enterpriseIds
          : undefined,
      ),
      filter(isDefined),
    );

    const enterpriseCollection =
      this.datastore.collection<EnterprisesCollection>('enterprises', query =>
        this.showPoolLists$.pipe(
          map(showPoolLists =>
            showPoolLists
              ? query.where('id', 'in', enterpriseIds$)
              : query.null(),
          ),
        ),
      );

    /**
     * Create a mapping of enterprise names. Users should only have one enterprise,
     * but this should support multiple
     */
    const enterpriseNamesMap$ = enterpriseCollection
      .valueChanges()
      .pipe(
        map(enterprises =>
          enterprises?.length > 0
            ? new Map<string, boolean>(
                enterprises.map(enterprise => [enterprise.internalName, true]),
              )
            : undefined,
        ),
      );

    /**
     * Gets this the same way that the logo component gets enterprise logos,
     * but should be from the enterprise (or ideally the pool)
     */
    this.insourcePoolImage = this.assets.getAssetPath(
      'freelancer-enterprise-pool-icon.svg',
    );

    const isDeloitteDemo$ = this.localStorage
      .get('deloitteDemo')
      .pipe(map(isDeloitteDemo => !!isDeloitteDemo));

    const poolsCollection = this.datastore.collection<PoolsCollection>(
      'pools',
      query =>
        this.showPoolLists$.pipe(
          map(showPoolLists => (showPoolLists ? query : query.null())),
        ),
    );

    const favoritesListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_FAVORITES,
        { index: 'type' },
      );

    const myHiresListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_MY_HIRES,
        { index: 'type' },
      );

    const myClientsListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_MY_CLIENTS,
        { index: 'type' },
      );

    const followingList = this.datastore.collection<UsersFollowCollection>(
      'usersFollow',
      query => query.where('followedByUserId', '==', userId$),
    );

    const recentlyViewedListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_RECENTLY_VIEWED_USERS,
        { index: 'type' },
      );

    const groupPostsListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_GROUP_FEED_ITEMS,
        { index: 'type' },
      );

    const customListsCollection =
      this.datastore.collection<DiscoverCollectionsCollection>(
        'discoverCollections',
        query =>
          query
            .where('ownerId', '==', userId$)
            .where('type', '==', DiscoverCollectionTypeApi.LISTS_CUSTOM)
            .orderBy('name', OrderByDirection.ASC, ORDER_BY_OPTIONS_UNICODE_CI)
            .limit(
              this.listViewLimitSubject$.pipe(
                map(limit => limit + this.VIEW_MORE_INCREMENT + 1),
              ),
            ),
      );

    // Fetch enterprise insource internal talent directories
    this.insourcePool$ = combineLatest([
      this.showPoolLists$,
      enterpriseNamesMap$,
      poolsCollection.valueChanges(),
    ]).pipe(
      map(([showPoolLists, enterpriseNamesMap, pools]) =>
        showPoolLists
          ? pools.find(
              pool =>
                enterpriseNamesMap?.has(pool.name) &&
                pool.isInsourceEnabled &&
                !pool.isTalentNetwork,
            )
          : undefined,
      ),
      filter(isDefined),
      map(pool => {
        // TODO: T267853 - Replace with a display name mapping
        return pool.id === Pool.DELOITTE_DC
          ? { ...pool, displayName: 'Deloitte Gig Workers' }
          : pool;
      }),
    );

    // Fetch enterprise talent networks
    this.talentNetworkPools$ = combineLatest([
      this.showPoolLists$,
      isDeloitteDemo$,
      poolsCollection.valueChanges(),
    ]).pipe(
      map(([showPoolLists, isDeloitteDemo, pools]) => {
        return pools.filter(pool => {
          // only filter by valid pools
          if (
            !showPoolLists ||
            !pool.isTalentNetwork ||
            pool.isInsourceEnabled
          ) {
            return false;
          }

          // enable deloitte pool only if deloitteDemo is enabled
          if (!isDeloitteDemo && Pool.DELOITTE_FTN === pool.id) {
            return false;
          }

          return true;
        });
      }),
    );

    this.favoritesList$ = favoritesListDocument.valueChanges();
    this.myHiresList$ = myHiresListDocument.valueChanges();
    this.myClientsList$ = myClientsListDocument.valueChanges();
    this.showFollowing$ = followingList.valueChanges().pipe(
      map(following => following.length > 0),
      startWith(false),
    );
    this.recentlyViewedList$ = recentlyViewedListDocument.valueChanges();
    this.groupPostsList$ = groupPostsListDocument.valueChanges();
    this.customLists$ = customListsCollection.valueChanges();

    this.customListsToShow$ = combineLatest([
      this.customLists$,
      this.listViewLimit$,
    ]).pipe(map(([customLists, limit]) => customLists.slice(0, limit)));

    const enterpriseError$ = enterpriseCollection.status$.pipe(
      map(status => status.error),
    );

    const poolError$ = poolsCollection.status$.pipe(
      map(status => status.error),
    );

    const favoritesListError$ = favoritesListDocument.status$.pipe(
      map(status => status.error),
    );

    const myHiresListError$ = myHiresListDocument.status$.pipe(
      map(status => status.error),
    );

    const myClientsListError$ = myClientsListDocument.status$.pipe(
      map(status => status.error),
    );

    const recentlyViewedListError$ = recentlyViewedListDocument.status$.pipe(
      map(status => status.error),
    );

    const groupPostsListError$ = groupPostsListDocument.status$.pipe(
      map(status => status.error),
    );

    const customListsError$ = customListsCollection.status$.pipe(
      map(status => status.error),
    );

    const enterpriseLoading$ = enterpriseCollection.status$.pipe(
      map(status => !status.error && !status.ready),
    );

    const poolsLoading$ = combineLatest([
      poolsCollection.status$,
      poolsCollection.valueChanges().pipe(startWith(undefined)),
    ]).pipe(
      map(([status, pools]) => !status.error && (!status.ready || !pools)),
    );

    const favoritesListLoading$ = combineLatest([
      this.favoritesList$.pipe(startWith(undefined)),
      favoritesListDocument.status$,
    ]).pipe(
      map(
        ([favoritesList, status]) =>
          !status.error && (!status.ready || !favoritesList),
      ),
    );

    const myHiresListLoading$ = combineLatest([
      this.myHiresList$.pipe(startWith(undefined)),
      myHiresListDocument.status$,
    ]).pipe(
      map(
        ([myHiresList, status]) =>
          !status.error && (!status.ready || !myHiresList),
      ),
    );

    const myClientsListLoading$ = combineLatest([
      this.myClientsList$.pipe(startWith(undefined)),
      myClientsListDocument.status$,
    ]).pipe(
      map(
        ([myClientsList, status]) =>
          !status.error && (!status.ready || !myClientsList),
      ),
    );

    const recentlyViewedListLoading$ = combineLatest([
      this.recentlyViewedList$.pipe(startWith(undefined)),
      recentlyViewedListDocument.status$,
    ]).pipe(
      map(
        ([recentlyViewedList, status]) =>
          !status.error && (!status.ready || !recentlyViewedList),
      ),
    );

    const groupPostsListLoading$ = combineLatest([
      this.groupPostsList$.pipe(startWith(undefined)),
      groupPostsListDocument.status$,
    ]).pipe(
      map(
        ([groupPostsList, status]) =>
          !status.error && (!status.ready || !groupPostsList),
      ),
    );

    const customListsLoading$ = combineLatest([
      customListsCollection.status$,
      this.customLists$.pipe(startWith(undefined)),
      this.shouldShowInitialLoadingState$,
    ]).pipe(
      map(([status, customCollection, shouldShowLoading]) =>
        shouldShowLoading
          ? !status.error && (!status.ready || !customCollection)
          : false,
      ),
    );

    this.userCollectionLoading$ = combineLatest([
      enterpriseLoading$,
      poolsLoading$,
      favoritesListLoading$,
      myHiresListLoading$,
      myClientsListLoading$,
      recentlyViewedListLoading$,
      groupPostsListLoading$,
      customListsLoading$,
    ]).pipe(
      map(loadingStates => loadingStates.some(ls => ls)),
      startWith(true),
    );

    this.userCollectionError$ = combineLatest([
      enterpriseError$,
      poolError$,
      favoritesListError$,
      myHiresListError$,
      myClientsListError$,
      recentlyViewedListError$,
      groupPostsListError$,
      customListsError$,
    ]).pipe(map(errorStates => errorStates.find(es => es !== undefined)));

    this.showViewMore$ = combineLatest([
      this.customLists$,
      this.listViewLimit$,
    ]).pipe(map(([customLists, limit]) => customLists.length > limit));
  }

  viewMoreClick(): void {
    this.shouldShowInitialLoadingStateSubject$.next(false);

    firstValueFrom(
      this.listViewLimitSubject$.asObservable().pipe(untilDestroyed(this)),
    ).then(limit => {
      this.listViewLimitSubject$.next(limit + this.VIEW_MORE_INCREMENT);
    });
  }
}
